// Theme + global toast (undo-able) — lightweight, no dependencies.
import { useEffect, useState, useCallback, useRef } from "react";

const THEME_KEY = "tw_theme";

/** Read/persist theme. Returns ['light'|'dark', setter]. */
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  return [theme, toggle];
}

/** Tiny pub-sub for toasts (no provider needed). */
const toastListeners = new Set();
export function showToast(toast) {
  const id = Date.now() + Math.random();
  const t = { id, ...toast };
  toastListeners.forEach((cb) => cb({ type: "add", toast: t }));
  return id;
}
export function removeToast(id) {
  toastListeners.forEach((cb) => cb({ type: "remove", id }));
}

/** Hook used by <Toaster/> to subscribe. */
export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});
  useEffect(() => {
    const cb = (msg) => {
      if (msg.type === "add") {
        setToasts((cur) => [...cur, msg.toast]);
        // Auto-dismiss
        const ttl = msg.toast.duration || 5000;
        timers.current[msg.toast.id] = setTimeout(() => {
          setToasts((cur) => cur.filter((t) => t.id !== msg.toast.id));
          if (msg.toast.onTimeout) msg.toast.onTimeout();
        }, ttl);
      } else if (msg.type === "remove") {
        setToasts((cur) => cur.filter((t) => t.id !== msg.id));
        clearTimeout(timers.current[msg.id]);
      }
    };
    toastListeners.add(cb);
    return () => toastListeners.delete(cb);
  }, []);
  const dismiss = useCallback((id) => removeToast(id), []);
  return [toasts, dismiss];
}

/** Helper: schedule a destructive action with a 5-sec Undo window.
 * onConfirm() runs only if the user does NOT click Undo within `duration` ms.
 * Returns the toast id.
 */
export function confirmWithUndo({ message, onConfirm, onUndo, duration = 5000 }) {
  let undone = false;
  const id = showToast({
    message,
    undoable: true,
    duration,
    onUndo: () => { undone = true; if (onUndo) onUndo(); },
    onTimeout: () => { if (!undone) onConfirm(); },
  });
  return id;
}
